import { Page, PageBody, PageHeader } from "@/components/app-layout";
import { TableSkeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useGrantsData } from "@/data/use-grants-data";
import { useGroupData } from "@/data/use-groups-data";
import { useKitsData } from "@/data/use-kits-data";
import { useParams } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";

const PRESET_FILTER = { type: "project_preset" as const };

// ============================================================
// Preset Row
// ============================================================

const PresetRow = ({
  preset,
  parentGroupName,
  groupUuid,
}: {
  preset: { uuid: string; name: string; description: string | null };
  parentGroupName: string;
  groupUuid: string;
}) => {
  const { grants, upsert, revoke } = useGrantsData({
    kitUuid: preset.uuid,
  });
  const [isPending, setIsPending] = useState(false);
  const isEnabled = grants.some((grant) => grant.group.uuid === groupUuid);

  const handleToggle = async (checked: boolean) => {
    setIsPending(true);
    try {
      if (checked) {
        await upsert({ groupUuid, access: "write" });
      } else {
        await revoke({ groupUuid });
      }
    } catch {
      toast.error("Failed to update preset access.");
    } finally {
      setIsPending(false);
    }
  };

  return (
    <TableRow>
      <TableCell className="font-medium">{preset.name}</TableCell>
      <TableCell className="text-muted-foreground">
        {preset.description}
      </TableCell>
      <TableCell>{parentGroupName}</TableCell>
      <TableCell>
        <Switch
          checked={isEnabled}
          disabled={isPending}
          onCheckedChange={handleToggle}
        />
      </TableCell>
    </TableRow>
  );
};

// ============================================================
// Page
// ============================================================

export const GlobalAdminGroupPresetsPage = () => {
  const { groupUuid } = useParams({ strict: false }) as {
    groupUuid?: string;
  };
  const { group } = useGroupData(groupUuid);
  const parentGroupUuid = group?.parentGroupUuid ?? null;
  const { group: parentGroup } = useGroupData(parentGroupUuid);
  const { kits: parentPresets, isLoading } = useKitsData(
    parentGroupUuid,
    PRESET_FILTER,
  );

  if (!group) return null;

  return (
    <Page>
      <PageHeader>
        <PageHeader.Title>Presets</PageHeader.Title>
      </PageHeader>
      <PageBody>
        {!parentGroupUuid && (
          <p className="text-muted-foreground text-sm">
            This group has no parent group. There are no parent artifacts to
            manage.
          </p>
        )}
        {parentGroupUuid && (
          <p className="text-muted-foreground text-sm">
            These presets are originally shared or published in the parent group{" "}
            <span className="text-foreground font-medium">
              {parentGroup?.name ?? parentGroupUuid}
            </span>
            .
          </p>
        )}
        {parentGroupUuid && isLoading && <TableSkeleton />}
        {parentGroupUuid && !isLoading && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Group</TableHead>
                <TableHead>Access</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {parentPresets.map((preset) => (
                <PresetRow
                  key={preset.uuid}
                  preset={preset}
                  parentGroupName={parentGroup?.name ?? parentGroupUuid}
                  groupUuid={groupUuid!}
                />
              ))}
              {parentPresets.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    className="text-muted-foreground py-4 text-center"
                  >
                    No presets in the parent group.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </PageBody>
    </Page>
  );
};
